import { CommonModule, formatDate } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subscription, forkJoin, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { RadioButtonModule } from 'primeng/radiobutton';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { TableModule } from 'primeng/table';

import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { SessionStorageService } from '../../services/session-storage.service';
import {
  EstimatedImpacts,
  RouteStop,
  ScoreBreakdown,
  ShipmentDeliveryReleaseAmendment,
  ShipmentMatchPage,
  ShipmentMatchView,
  ShipmentReleaseAttachmentPreview,
  TransportOrderRelease
} from '../transport-planning.model';
import { TransportPlanningService } from '../transport-planning.service';

export type AddToShipmentStep = 'MATCH' | 'REVIEW' | 'CONFIRMATION';

export interface CombinedTargetPreview {
  targetKey: string;
  shipmentId: number;
  shipmentNumber: string;
  deliveryId?: number;
  deliveryNumber?: string;
  stage: string;
  assignedReleaseIds: number[];
  assignedReleases: TransportOrderRelease[];
  attachmentPreview?: ShipmentReleaseAttachmentPreview;
  deliveryAmendment?: ShipmentDeliveryReleaseAmendment;
  status: 'IDLE' | 'LOADING' | 'PREVIEWED' | 'APPLYING' | 'CONFIRMED' | 'FAILED' | 'EXPIRED';
  errorMessage?: string;
  confirmedBuyingPrice?: number;
  confirmedSellingPrice?: number;
  amendmentReason?: string;
}

export interface ConfirmedShipmentSummary {
  shipmentId: number;
  shipmentNumber: string;
  deliveryId?: number;
  deliveryNumber?: string;
  stage: string;
  releaseNumbers: string[];
}

@Component({
  selector: 'app-transport-release-add-to-shipment',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    RadioButtonModule,
    TagModule,
    TooltipModule,
    TableModule,
    PageShellComponent,
    PageHeroComponent,
    PageDataPanelComponent
  ],
  templateUrl: './transport-release-add-to-shipment.component.html',
  styleUrls: ['./transport-release-add-to-shipment.component.css']
})
export class TransportReleaseAddToShipmentComponent implements OnInit, OnDestroy {
  step: AddToShipmentStep = 'MATCH';
  isLoading = false;
  message = '';
  errorMessage = '';

  releaseIds: number[] = [];
  releases: TransportOrderRelease[] = [];
  owningCompanyId: number = 0;

  // Step 1: Match state
  matchesByReleaseId = new Map<number, ShipmentMatchPage>();
  matchLoadingByReleaseId = new Map<number, boolean>();
  selectedCandidateByReleaseId = new Map<number, ShipmentMatchView | null>();

  // Step 2: Combined Review state
  targetPreviews: CombinedTargetPreview[] = [];

  // Step 3: Confirmation outcomes
  confirmedShipments: ConfirmedShipmentSummary[] = [];

  private querySub?: Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private transportPlanningService: TransportPlanningService,
    private sessionStorageService: SessionStorageService
  ) {}

  ngOnInit(): void {
    this.owningCompanyId = Number(this.sessionStorageService.getCompany()?.id || 0);

    this.querySub = this.route.queryParamMap.subscribe(params => {
      const idsParam = params.get('releaseIds');
      if (!idsParam) {
        this.errorMessage = 'No order releases specified. Return to the Release Pool to select releases.';
        return;
      }
      this.releaseIds = idsParam
        .split(',')
        .map(id => Number(id.trim()))
        .filter(id => !isNaN(id) && id > 0);

      if (!this.releaseIds.length) {
        this.errorMessage = 'No valid order releases provided.';
        return;
      }
      this.loadReleasesAndMatches();
    });
  }

  ngOnDestroy(): void {
    this.querySub?.unsubscribe();
  }

  // --- Loading Releases and Initial Matches ---

  loadReleasesAndMatches(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.message = '';

    forkJoin(this.releaseIds.map(id => this.transportPlanningService.getRelease(id).pipe(catchError(() => of(null)))))
      .pipe(
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({
        next: results => {
          this.releases = results.filter((r): r is TransportOrderRelease => r !== null && r.releaseId !== undefined);
          if (!this.releases.length) {
            this.errorMessage = 'Selected order releases could not be found.';
            return;
          }
          if (this.releases[0].owningCompanyId) {
            this.owningCompanyId = this.releases[0].owningCompanyId;
          }
          // Fetch candidate matches for each release
          for (const release of this.releases) {
            if (release.releaseId) {
              this.loadMatchesForRelease(release.releaseId, 0);
            }
          }
        },
        error: () => {
          this.errorMessage = 'Could not load the selected order releases.';
        }
      });
  }

  loadMatchesForRelease(releaseId: number, page: number): void {
    this.matchLoadingByReleaseId.set(releaseId, true);
    this.transportPlanningService
      .getReleaseMatches(releaseId, this.owningCompanyId, page, 5)
      .pipe(
        finalize(() => {
          this.matchLoadingByReleaseId.set(releaseId, false);
        })
      )
      .subscribe({
        next: matchPage => {
          this.matchesByReleaseId.set(releaseId, matchPage);
          // Auto-select top match if not yet selected and eligible
          if (!this.selectedCandidateByReleaseId.has(releaseId) && matchPage.items?.length > 0) {
            const topMatch = matchPage.items[0];
            if (topMatch.eligible !== false) {
              this.selectedCandidateByReleaseId.set(releaseId, topMatch);
            }
          }
        },
        error: () => {
          // If match fails, record empty page
          this.matchesByReleaseId.set(releaseId, {
            releaseId,
            companyId: this.owningCompanyId,
            items: [],
            totalRecords: 0,
            page: 0,
            pageSize: 5,
            totalPages: 0
          });
        }
      });
  }

  // --- Step 1: Match Actions ---

  candidateKey(candidate: ShipmentMatchView | null): string {
    if (!candidate) return 'unassigned';
    return candidate.deliveryId
      ? `delivery-${candidate.shipmentId}-${candidate.deliveryId}`
      : `shipment-${candidate.shipmentId}`;
  }

  isCandidateSelected(releaseId: number, candidate: ShipmentMatchView | null): boolean {
    const selected = this.selectedCandidateByReleaseId.get(releaseId) ?? null;
    return this.candidateKey(selected) === this.candidateKey(candidate);
  }

  selectCandidate(releaseId: number, candidate: ShipmentMatchView | null): void {
    this.selectedCandidateByReleaseId.set(releaseId, candidate);
  }

  assignedReleasesCount(): number {
    let count = 0;
    for (const release of this.releases) {
      if (release.releaseId && this.selectedCandidateByReleaseId.get(release.releaseId)) {
        count++;
      }
    }
    return count;
  }

  canProceedToReview(): boolean {
    return this.assignedReleasesCount() > 0;
  }

  proceedToReview(): void {
    if (!this.canProceedToReview()) {
      this.errorMessage = 'Assign at least one release to an existing shipment or delivery.';
      return;
    }

    // Group provisional assignments by target shipment (+ optional delivery)
    const groupsMap = new Map<string, {
      shipmentId: number;
      shipmentNumber: string;
      deliveryId?: number;
      deliveryNumber?: string;
      stage: string;
      releases: TransportOrderRelease[];
    }>();

    for (const release of this.releases) {
      if (!release.releaseId) continue;
      const candidate = this.selectedCandidateByReleaseId.get(release.releaseId);
      if (!candidate) continue;

      const key = this.candidateKey(candidate);
      let group = groupsMap.get(key);
      if (!group) {
        group = {
          shipmentId: candidate.shipmentId,
          shipmentNumber: candidate.shipmentNumber,
          deliveryId: candidate.deliveryId,
          deliveryNumber: candidate.deliveryNumber,
          stage: candidate.stage,
          releases: []
        };
        groupsMap.set(key, group);
      }
      group.releases.push(release);
    }

    this.targetPreviews = Array.from(groupsMap.entries()).map(([key, group]) => ({
      targetKey: key,
      shipmentId: group.shipmentId,
      shipmentNumber: group.shipmentNumber,
      deliveryId: group.deliveryId,
      deliveryNumber: group.deliveryNumber,
      stage: group.stage,
      assignedReleaseIds: group.releases
        .map(r => r.releaseId!)
        .filter((id): id is number => typeof id === 'number'),
      assignedReleases: group.releases,
      status: 'IDLE'
    }));

    this.step = 'REVIEW';
    this.errorMessage = '';
    this.message = '';

    // Fetch previews for each target group
    for (const target of this.targetPreviews) {
      this.fetchPreviewForTarget(target);
    }
  }

  backToMatch(): void {
    this.step = 'MATCH';
    this.errorMessage = '';
    this.message = '';
  }

  // --- Step 2: Review & Previews ---

  fetchPreviewForTarget(target: CombinedTargetPreview): void {
    target.status = 'LOADING';
    target.errorMessage = undefined;

    if (target.deliveryId) {
      // Existing delivery preview
      this.transportPlanningService
        .previewDeliveryReleaseAmendment(target.shipmentId, target.deliveryId, target.assignedReleaseIds)
        .subscribe({
          next: amendment => {
            target.deliveryAmendment = amendment;
            target.status = amendment.status === 'EXPIRED' ? 'EXPIRED' : 'PREVIEWED';
            target.confirmedBuyingPrice = amendment.proposedBuyingPrice ?? 0;
            target.confirmedSellingPrice = amendment.proposedSellingPrice ?? 0;
            target.amendmentReason = amendment.reason || 'Added selected order releases';
          },
          error: err => {
            target.status = 'FAILED';
            target.errorMessage = err?.error?.message || err?.message || 'Could not generate delivery amendment preview.';
          }
        });
    } else {
      // Shipment without delivery preview
      this.transportPlanningService
        .previewReleaseAttachment(target.shipmentId, target.assignedReleaseIds)
        .subscribe({
          next: preview => {
            target.attachmentPreview = preview;
            target.status = preview.status === 'EXPIRED' ? 'EXPIRED' : 'PREVIEWED';
          },
          error: err => {
            target.status = 'FAILED';
            target.errorMessage = err?.error?.message || err?.message || 'Could not generate shipment attachment preview.';
          }
        });
    }
  }

  refreshPreview(target: CombinedTargetPreview): void {
    this.fetchPreviewForTarget(target);
  }

  confirmTarget(target: CombinedTargetPreview): void {
    if (target.status === 'APPLYING' || target.status === 'CONFIRMED') return;

    target.status = 'APPLYING';
    target.errorMessage = undefined;

    if (target.deliveryId) {
      const amendmentId = target.deliveryAmendment?.amendmentId;
      if (!amendmentId) {
        target.status = 'FAILED';
        target.errorMessage = 'Missing amendment preview ID. Please refresh the review.';
        return;
      }
      if (!target.amendmentReason || !target.amendmentReason.trim()) {
        target.status = 'FAILED';
        target.errorMessage = 'Please provide a valid reason for amending this delivery.';
        return;
      }

      this.transportPlanningService
        .applyDeliveryReleaseAmendment(target.shipmentId, target.deliveryId, amendmentId, {
          reason: target.amendmentReason.trim(),
          confirmedBuyingPrice: target.confirmedBuyingPrice ?? 0,
          confirmedSellingPrice: target.confirmedSellingPrice ?? 0
        })
        .subscribe({
          next: result => {
            target.deliveryAmendment = result;
            target.status = 'CONFIRMED';
            this.recordConfirmation(target);
          },
          error: err => {
            target.status = 'FAILED';
            target.errorMessage = err?.error?.message || err?.message || 'Failed to apply delivery amendment.';
          }
        });
    } else {
      const attachmentId = target.attachmentPreview?.attachmentId;
      if (!attachmentId) {
        target.status = 'FAILED';
        target.errorMessage = 'Missing attachment preview ID. Please refresh the review.';
        return;
      }

      this.transportPlanningService
        .applyReleaseAttachment(target.shipmentId, attachmentId, {})
        .subscribe({
          next: result => {
            target.attachmentPreview = result;
            target.status = 'CONFIRMED';
            this.recordConfirmation(target);
          },
          error: err => {
            target.status = 'FAILED';
            target.errorMessage = err?.error?.message || err?.message || 'Failed to apply shipment release attachment.';
          }
        });
    }
  }

  private recordConfirmation(target: CombinedTargetPreview): void {
    const existing = this.confirmedShipments.find(c => c.shipmentId === target.shipmentId);
    if (!existing) {
      this.confirmedShipments.push({
        shipmentId: target.shipmentId,
        shipmentNumber: target.shipmentNumber,
        deliveryId: target.deliveryId,
        deliveryNumber: target.deliveryNumber,
        stage: target.stage,
        releaseNumbers: target.assignedReleases.map(r => r.releaseNumber || ('Release #' + r.releaseId))
      });
    }
    // If all targets are confirmed, transition automatically to Confirmation
    if (this.allTargetsConfirmed()) {
      this.step = 'CONFIRMATION';
    }
  }

  allTargetsConfirmed(): boolean {
    return this.targetPreviews.length > 0 && this.targetPreviews.every(t => t.status === 'CONFIRMED');
  }

  hasAnyConfirmedTarget(): boolean {
    return this.confirmedShipments.length > 0;
  }

  hasFailedTargets(): boolean {
    return this.targetPreviews.some(t => t.status === 'FAILED' || t.status === 'EXPIRED');
  }

  goToConfirmation(): void {
    this.step = 'CONFIRMATION';
  }

  // --- Step 3: Confirmation Outcomes ---

  returnToReleaseList(): void {
    this.router.navigate(['/transport-planning/releases']);
  }

  // --- Helper formatting ---

  formatDelta(val?: number, unit = ''): string {
    if (val === undefined || val === null) return '0 ' + unit;
    const sign = val > 0 ? '+' : '';
    return `${sign}${val.toFixed(1)} ${unit}`.trim();
  }

  formatMinutes(val?: number): string {
    if (val === undefined || val === null) return '0 min';
    const sign = val > 0 ? '+' : '';
    return `${sign}${Math.round(val)} min`;
  }

  formatDate(date?: string | Date): string {
    if (!date) return '-';
    return formatDate(date, 'dd MMM HH:mm', 'en-US');
  }

  stopStatusSeverity(status: string): 'success' | 'info' | 'warn' | 'secondary' {
    switch (status) {
      case 'ADDED':
        return 'success';
      case 'REUSED':
        return 'info';
      case 'REORDERED':
        return 'warn';
      default:
        return 'secondary';
    }
  }

  fitPercentageSeverity(fit: number): 'success' | 'info' | 'warn' | 'danger' {
    if (fit >= 80) return 'success';
    if (fit >= 60) return 'info';
    if (fit >= 40) return 'warn';
    return 'danger';
  }

  releaseWeightSummary(release: TransportOrderRelease): string {
    const totalWeight = release.lines?.reduce((sum, line) => sum + (line.weightKg || 0), 0) || 0;
    return totalWeight > 0 ? `${totalWeight} kg` : 'Weight unverified';
  }

  releasePalletSummary(release: TransportOrderRelease): string {
    const totalPallets = release.lines?.reduce((sum, line) => sum + (line.pallets || 0), 0) || 0;
    return totalPallets > 0 ? `${totalPallets} pallets` : 'Pallets unverified';
  }
}
